// src/ConsumerApp/Screens/Home/components/LiveOrderTrackerBanner.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// Real SVGs from assets/icons
import CaretRight from '../../../../assets/icons/CaretRight.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

function OrderThumb({ imagePath, alt, zIndex }) {
  const [hasError, setHasError] = useState(false);

  let cleanPath = imagePath || '';
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.slice(1);
  }

  const src = cleanPath
    ? cleanPath.startsWith('http')
      ? cleanPath
      : `${IMAGE_BASE_URL}${cleanPath}`
    : null;

  return (
    <div
      className="bb-thumb-avatar"
      style={{ zIndex }}
    >
      {src && !hasError ? (
        <img
          src={src}
          alt={alt || 'material'}
          className="bb-thumb-img"
          onError={() => setHasError(true)}
        />
      ) : (
        <img src={ToteIcon} alt="material" className="bb-thumb-fallback" />
      )}
    </div>
  );
}

export default function LiveOrderTrackerBanner({ activeOrder }) {
  const navigate = useNavigate();

  if (!activeOrder) return null;

  const orderId = activeOrder.order_id || activeOrder._id;
  const orderNumber = activeOrder.order_number || String(orderId || '').slice(-6).toUpperCase();
  const totalAmount = Number(activeOrder.total || 0);
  const items = Array.isArray(activeOrder.items) ? activeOrder.items : [];
  const itemsCount = activeOrder.items_count || items.length || 0;

  // Show up to 2 circular images, with "+N" badge if more exist
  const maxDisplay = 2;
  const displayItems = items.slice(0, maxDisplay);
  const extraCount = itemsCount - displayItems.length;

  const handleTrackClick = () => {
    navigate(`/consumer/account/orders/${orderId}`);
  };

  return (
    <>
      <style>{`
        /* ══════════════════════════════════════════════════════════
           BASE COMPONENT STYLES (DESKTOP & GENERAL)
           ══════════════════════════════════════════════════════════ */
        .bb-live-order-banner {
          background-color: #0F172A;
          border-radius: 14px;
          padding: 12px 16px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          cursor: pointer;
          box-shadow: 0 4px 18px rgba(15, 23, 42, 0.18);
          margin-bottom: 10px;
          width: 100%;
          box-sizing: border-box;
          border: 1px solid #1E293B;
          gap: 12px;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
          overflow: hidden;
        }

        .bb-live-order-banner:hover {
          box-shadow: 0 6px 22px rgba(15, 23, 42, 0.28);
        }

        .bb-live-left {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
          min-width: 0; /* Critical for inner text truncation */
        }

        /* ── Overlapping Avatar Stack ── */
        .bb-avatar-stack {
          display: flex;
          align-items: center;
          padding-left: 2px;
          flex-shrink: 0;
        }

        .bb-thumb-avatar {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background-color: #FFFFFF;
          border: 2px solid #0F172A;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
          transition: width 0.15s ease, height 0.15s ease;
        }

        .bb-avatar-stack > .bb-thumb-avatar:not(:first-child),
        .bb-avatar-stack > .bb-extra-avatar-badge {
          margin-left: -12px;
        }

        .bb-thumb-img {
          width: 85%;
          height: 85%;
          object-fit: contain;
          display: block;
        }

        .bb-thumb-fallback {
          width: 16px;
          height: 16px;
          opacity: 0.4;
          display: block;
        }

        .bb-extra-avatar-badge {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          background-color: #1E293B;
          border: 2px solid #0F172A;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #94A3B8;
          font-size: 11px;
          font-weight: 800;
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.35);
          flex-shrink: 0;
          z-index: 1;
        }

        /* ── Center Text Cluster ── */
        .bb-live-text-cluster {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0; /* Prevents overflow blowout */
        }

        .bb-live-status-row {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 0;
        }

        .bb-live-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background-color: #22C55E;
          box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.22);
          display: inline-block;
          flex-shrink: 0;
        }

        .bb-live-heading {
          margin: 0;
          font-size: 13.5px;
          font-weight: 800;
          color: #F8FAFC;
          letter-spacing: 0.1px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .bb-live-order-badge {
          font-size: 11px;
          font-weight: 600;
          color: #94A3B8;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .bb-live-subtext {
          font-size: 11.5px;
          color: #94A3B8;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ── Right Cluster ── */
        .bb-live-right {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
        }

        .bb-live-meta-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
        }

        .bb-live-meta-items {
          font-size: 10px;
          font-weight: 600;
          color: #94A3B8;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .bb-live-meta-price {
          font-size: 13px;
          font-weight: 800;
          color: #F1F5F9;
          white-space: nowrap;
        }

        .bb-live-track-btn {
          background-color: #E9001E;
          color: #FFFFFF;
          border: none;
          border-radius: 8px;
          padding: 7px 12px;
          font-size: 11.5px;
          font-weight: 800;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
          box-shadow: 0 2px 6px rgba(233, 0, 30, 0.3);
          transition: opacity 0.15s ease;
          flex-shrink: 0;
        }

        .bb-live-track-btn:hover {
          opacity: 0.9;
        }

        /* ══════════════════════════════════════════════════════════
           RESPONSIVE BREAKPOINTS (TABLETS & FOLDABLES <= 768px)
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 768px) {
          .bb-live-order-banner {
            padding: 10px 14px;
            gap: 10px;
          }
          .bb-thumb-avatar,
          .bb-extra-avatar-badge {
            width: 34px;
            height: 34px;
          }
          .bb-avatar-stack > .bb-thumb-avatar:not(:first-child),
          .bb-avatar-stack > .bb-extra-avatar-badge {
            margin-left: -10px;
          }
        }

        /* ══════════════════════════════════════════════════════════
           MOBILE BREAKPOINTS (STANDARD PHONES <= 480px)
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 480px) {
          .bb-live-order-banner {
            padding: 10px 12px;
            gap: 8px;
          }
          .bb-live-meta-col {
            display: none; /* Drop secondary price info to guarantee button visibility */
          }
          .bb-live-subtext {
            font-size: 11px;
          }
        }

        /* ══════════════════════════════════════════════════════════
           COMPACT PHONES & NARROW DEVICES (<= 360px: e.g. iPhone SE)
           ══════════════════════════════════════════════════════════ */
        @media (max-width: 360px) {
          .bb-thumb-avatar,
          .bb-extra-avatar-badge {
            width: 30px;
            height: 30px;
          }
          .bb-avatar-stack > .bb-thumb-avatar:not(:first-child),
          .bb-avatar-stack > .bb-extra-avatar-badge {
            margin-left: -8px;
          }
          .bb-live-order-badge {
            display: none; /* Drop order number on narrow screens to avoid collision */
          }
          .bb-live-track-btn {
            padding: 6px 10px;
            font-size: 11px;
          }
        }
      `}</style>

      <div className="bb-live-order-banner" onClick={handleTrackClick}>
        <div className="bb-live-left">
          {/* Overlapping Product Images Stack */}
          <div className="bb-avatar-stack">
            {displayItems.map((item, idx) => (
              <OrderThumb
                key={item.item_id || idx}
                imagePath={item.item_image}
                alt={item.item_name}
                zIndex={displayItems.length - idx + 1}
              />
            ))}
            {extraCount > 0 && (
              <div className="bb-extra-avatar-badge">
                +{extraCount}
              </div>
            )}
          </div>

          {/* Status Label & Micro-copy */}
          <div className="bb-live-text-cluster">
            <div className="bb-live-status-row">
              <span className="bb-live-pulse-dot" />
              <h4 className="bb-live-heading">Order in Progress</h4>
              <span className="bb-live-order-badge">#{orderNumber}</span>
            </div>
            <span className="bb-live-subtext">Tap to view live delivery status & details</span>
          </div>
        </div>

        {/* Pricing & Track CTA */}
        <div className="bb-live-right">
          {totalAmount > 0 && (
            <div className="bb-live-meta-col">
              {itemsCount > 0 && (
                <span className="bb-live-meta-items">
                  {itemsCount} {itemsCount === 1 ? 'item' : 'items'}
                </span>
              )}
              <span className="bb-live-meta-price">₹{totalAmount.toLocaleString('en-IN')}</span>
            </div>
          )}

          <button
            type="button"
            className="bb-live-track-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleTrackClick();
            }}
          >
            <span>Track</span>
            <img
              src={CaretRight}
              alt="→"
              style={{ width: 10, height: 10, filter: 'brightness(0) invert(1)' }}
            />
          </button>
        </div>
      </div>
    </>
  );
}